import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    Plus,
    Loader2,
    AlertTriangle,
    Ban,
    User as UserIcon,
    Search,
    Trash2,
    AlertCircle,
    CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SortableRankedRow } from '@/components/bets/SortableRankedRow';
import { RunnerActionSheet } from '@/components/bets/RunnerActionSheet';
import { PageContent } from '@/components/layout/PageContent';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { GET_RACE_DETAILS } from '@/graphql/queries';
import type { Gender } from '@/gql/graphql';

export interface SubmitResultsData {
    results: Array<{ runnerId: string; position: number }>;
    dnsRunnerIds: string[];
}

interface SubmitResultsProps {
    raceId: string;
    gender: Gender;
    /** Report current data to parent for cross-gender submit */
    onDataRef?: (fn: (() => SubmitResultsData) | null) => void;
    /** Report unplaced runner count to parent */
    onUnplacedChange?: (count: number) => void;
}

export function SubmitResults({ raceId, gender, onDataRef, onUnplacedChange }: SubmitResultsProps) {
    const { t } = useTranslation('races');
    const { t: tc } = useTranslation();
    const { t: tb } = useTranslation('bets');

    const [positions, setPositions] = useState<Map<string, number>>(new Map());
    const [dnsRunnerIds, setDnsRunnerIds] = useState<Set<string>>(new Set());
    const [selectedRunnerId, setSelectedRunnerId] = useState<string | null>(null);
    const [initialized, setInitialized] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAllRunners, setShowAllRunners] = useState(false);

    const { data, loading, error } = useQuery(GET_RACE_DETAILS, { variables: { id: raceId } });

    const race = data?.race;
    const allRunners = useMemo(() => (race?.runners || []).filter(r => r.gender === gender), [race?.runners, gender]);
    const runnerMap = useMemo(() => new Map(allRunners.map(r => [r.id!, r])), [allRunners]);

    // Pre-fill from existing results (render-time pattern)
    if (!initialized && !loading && race) {
        const initial = new Map<string, number>();
        const initialDns = new Set<string>();
        for (const r of allRunners) {
            if (r.id && r.result != null) {
                if (r.result === -1) initialDns.add(r.id);
                else initial.set(r.id, r.result);
            }
        }
        if (initial.size > 0) setPositions(initial);
        if (initialDns.size > 0) setDnsRunnerIds(initialDns);
        setInitialized(true);
    }

    // Derived lists
    const rankedEntries = useMemo(() =>
        [...positions.entries()].filter(([, pos]) => pos > 0).sort(([, a], [, b]) => a - b),
    [positions]);
    const dnfRunners = useMemo(() => allRunners.filter(r => r.id && positions.get(r.id) === 0), [allRunners, positions]);
    const dnsRunners = useMemo(() => allRunners.filter(r => r.id && dnsRunnerIds.has(r.id)), [allRunners, dnsRunnerIds]);
    const unrankedRunners = useMemo(() => [...allRunners]
        .filter(r => r.id && !positions.has(r.id) && !dnsRunnerIds.has(r.id))
        .sort((a, b) => (b.effectiveIndex || 0) - (a.effectiveIndex || 0)),
    [allRunners, positions, dnsRunnerIds]);

    const totalPlaced = rankedEntries.length + dnfRunners.length + dnsRunners.length;
    const unplacedCount = allRunners.length - totalPlaced;

    // Report unplaced count to parent
    useEffect(() => { onUnplacedChange?.(unplacedCount); }, [unplacedCount, onUnplacedChange]);

    // Expose current data to parent for combined submit
    const positionsRef = useRef(positions);
    const dnsRef = useRef(dnsRunnerIds);
    useEffect(() => { positionsRef.current = positions; }, [positions]);
    useEffect(() => { dnsRef.current = dnsRunnerIds; }, [dnsRunnerIds]);
    useEffect(() => {
        onDataRef?.(() => ({
            results: [...positionsRef.current.entries()].map(([runnerId, position]) => ({ runnerId, position })),
            dnsRunnerIds: [...dnsRef.current],
        }));
        return () => onDataRef?.(null);
    }, [onDataRef]);

    const takenPositions = new Set([...positions.values()].filter(p => p > 0));
    const getNextPosition = (): number => {
        if (takenPositions.size === 0) return 1;
        return Math.max(...takenPositions) + 1;
    };

    // Actions
    const setRunnerPosition = (runnerId: string, position: number) => {
        setDnsRunnerIds(prev => { const n = new Set(prev); n.delete(runnerId); return n; });
        setPositions(prev => { const n = new Map(prev); n.set(runnerId, position); return n; });
    };
    const addToRanked = (runnerId: string) => setRunnerPosition(runnerId, getNextPosition());
    const setAsDnf = useCallback((runnerId: string) => {
        setDnsRunnerIds(prev => { const n = new Set(prev); n.delete(runnerId); return n; });
        setPositions(prev => { const n = new Map(prev); n.set(runnerId, 0); return n; });
        setSelectedRunnerId(null);
    }, []);
    const setAsDns = useCallback((runnerId: string) => {
        setPositions(prev => { const n = new Map(prev); n.delete(runnerId); return n; });
        setDnsRunnerIds(prev => new Set(prev).add(runnerId));
        setSelectedRunnerId(null);
    }, []);
    const clearRunner = useCallback((runnerId: string) => {
        setPositions(prev => { const n = new Map(prev); n.delete(runnerId); return n; });
        setDnsRunnerIds(prev => { const n = new Set(prev); n.delete(runnerId); return n; });
        setSelectedRunnerId(null);
    }, []);

    // DnD
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = rankedEntries.findIndex(([id]) => id === active.id);
        const newIdx = rankedEntries.findIndex(([id]) => id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        const reordered = [...rankedEntries];
        const [moved] = reordered.splice(oldIdx, 1);
        reordered.splice(newIdx, 0, moved);
        const next = new Map(positions);
        reordered.forEach(([id], i) => next.set(id, i + 1));
        setPositions(next);
    };

    // (Submit is handled by the parent page which combines both genders)

    // Loading / Error
    if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-emerald-600" /></div>;
    if (error) return <PageContent width="standard" className="py-8"><ErrorBanner message={tc('error.generic')} /></PageContent>;

    const genderLabel = gender === 'MALE' ? tb('gender.MALE') : tb('gender.FEMALE');
    const selectedRunner = selectedRunnerId ? runnerMap.get(selectedRunnerId) : null;
    const selectedPosition = selectedRunnerId ? positions.get(selectedRunnerId) : undefined;
    const isSelectedRanked = selectedPosition != null && selectedPosition > 0;
    const isSelectedDns = selectedRunnerId != null && dnsRunnerIds.has(selectedRunnerId);
    const isSelectedDnf = selectedPosition === 0;
    const isSelectedUnranked = selectedRunnerId != null && selectedPosition == null && !isSelectedDns;

    // Search
    const INITIAL_DISPLAY_COUNT = 8;
    const searchTerm = searchQuery.trim().toLowerCase();
    const searchResults = searchTerm
        ? unrankedRunners.filter(r => r.name?.toLowerCase().includes(searchTerm))
        : showAllRunners ? unrankedRunners : unrankedRunners.slice(0, INITIAL_DISPLAY_COUNT);
    const hasMoreRunners = !searchTerm && !showAllRunners && unrankedRunners.length > INITIAL_DISPLAY_COUNT;

    return (
        <PageContent width="standard" className="space-y-3 pb-8">
            {/* Header */}
            <div className="flex items-center gap-3">
                <span className={cn(
                    "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide",
                    gender === 'MALE' ? "bg-blue-50 text-blue-700" : "bg-pink-50 text-pink-700"
                )}>{genderLabel}</span>
                <span className="text-sm text-slate-400 font-medium">{allRunners.length} {t('details.runners')}</span>
            </div>

            {/* Progress bar */}
            <div className={cn(
                "flex items-center gap-2 rounded-xl px-3 py-2 border",
                unplacedCount === 0 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-100"
            )}>
                {unplacedCount === 0
                    ? <CheckCircle2 size={16} className="text-emerald-600" />
                    : <AlertCircle size={16} className="text-amber-500" />
                }
                <span className="text-xs font-bold text-slate-700">
                    {totalPlaced}/{allRunners.length} {t('details.runners')}
                </span>
                <span className={cn("text-xs font-black ml-auto", unplacedCount === 0 ? "text-emerald-600" : "text-amber-600")}>
                    {unplacedCount === 0 ? t('results.allPlaced') : `${unplacedCount} ${t('results.remaining')}`}
                </span>
            </div>

            {/* Empty */}
            {allRunners.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                    <UserIcon size={32} className="mb-2 opacity-20" />
                    <p className="text-sm font-medium">{t('results.noRunners')}</p>
                </div>
            )}

            {/* ══════ RANKED LIST (DnD) ══════ */}
            {rankedEntries.length > 0 && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={rankedEntries.map(([id]) => id)} strategy={verticalListSortingStrategy}>
                        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-100">
                            {rankedEntries.map(([runnerId, position]) => {
                                const runner = runnerMap.get(runnerId);
                                if (!runner) return null;
                                return (
                                    <SortableRankedRow
                                        key={runnerId}
                                        id={runnerId}
                                        position={position}
                                        name={runner.name ?? ''}
                                        onTap={() => setSelectedRunnerId(runnerId)}
                                    />
                                );
                            })}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {/* ══════ DNF LIST ══════ */}
            {dnfRunners.length > 0 && (
                <div>
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-1 mb-1 flex items-center gap-1">
                        <AlertTriangle size={10} /> DNF ({dnfRunners.length})
                    </h3>
                    <div className="rounded-2xl bg-white border border-amber-100 shadow-sm overflow-hidden divide-y divide-slate-100">
                        {dnfRunners.map(runner => (
                            <button
                                key={runner.id}
                                onClick={() => setSelectedRunnerId(runner.id!)}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-amber-50/50 transition-colors"
                            >
                                <span className="w-7 h-7 rounded-lg bg-amber-100 text-amber-600 text-[10px] font-black flex items-center justify-center flex-shrink-0">DNF</span>
                                <span className="flex-1 min-w-0 text-sm font-semibold text-slate-700 truncate">{runner.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* ══════ DNS LIST ══════ */}
            {dnsRunners.length > 0 && (
                <div>
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-1 mb-1 flex items-center gap-1">
                        <Ban size={10} /> DNS ({dnsRunners.length})
                    </h3>
                    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100">
                        {dnsRunners.map(runner => (
                            <button
                                key={runner.id}
                                onClick={() => setSelectedRunnerId(runner.id!)}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors opacity-60"
                            >
                                <span className="w-7 h-7 rounded-lg bg-slate-200 text-slate-500 text-[10px] font-black flex items-center justify-center flex-shrink-0">DNS</span>
                                <span className="flex-1 min-w-0 text-sm font-semibold text-slate-600 truncate">{runner.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* ══════ ADD RUNNER (inline search) ══════ */}
            {unrankedRunners.length > 0 && (
                <div>
                    <div className="relative mb-2">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t('results.section.unranked')}
                            className="w-full h-10 pl-9 pr-3 bg-slate-50 border border-slate-100 rounded-xl text-sm text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                        />
                    </div>
                    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-100">
                        {searchResults.map(runner => (
                            <div key={runner.id} className="flex items-center gap-2 px-3 py-2">
                                <button
                                    onClick={() => setSelectedRunnerId(runner.id!)}
                                    className="flex-1 min-w-0 text-left hover:bg-slate-50 -mx-1 px-1 py-0.5 rounded-lg transition-colors"
                                >
                                    <span className="text-sm font-medium text-slate-700 truncate block">{runner.name}</span>
                                    <span className="text-[11px] text-slate-400">UTMB {Math.round(runner.utmbIndex || 0)}</span>
                                </button>
                                <button
                                    onClick={() => addToRanked(runner.id!)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm transition-all active:scale-90 flex-shrink-0"
                                    title={t('results.section.ranked')}
                                >
                                    <Plus size={16} />
                                </button>
                                <button
                                    onClick={() => setAsDnf(runner.id!)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-500 hover:bg-amber-100 transition-all active:scale-90 flex-shrink-0"
                                    title="DNF"
                                >
                                    <AlertTriangle size={14} />
                                </button>
                                <button
                                    onClick={() => setAsDns(runner.id!)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-400 hover:bg-slate-200 transition-all active:scale-90 flex-shrink-0"
                                    title="DNS"
                                >
                                    <Ban size={14} />
                                </button>
                            </div>
                        ))}
                        {searchResults.length === 0 && searchTerm && (
                            <div className="px-3 py-4 text-center text-xs text-slate-400">{tc('races:details.filter.noResults')}</div>
                        )}
                    </div>
                    {hasMoreRunners && (
                        <button
                            onClick={() => setShowAllRunners(true)}
                            className="w-full mt-2 py-2 text-xs font-bold text-slate-500 hover:text-emerald-600 transition-colors"
                        >
                            {t('details.liveTracking.showMore', { count: unrankedRunners.length - INITIAL_DISPLAY_COUNT })}
                        </button>
                    )}
                </div>
            )}

            {/* ══════ ACTION SHEET ══════ */}
            {selectedRunner && selectedRunnerId && (
                <RunnerActionSheet
                    runner={selectedRunner}
                    raceFormat={race?.raceFormat}
                    onClose={() => setSelectedRunnerId(null)}
                >
                    {/* Unranked: Classer + DNF + DNS */}
                    {isSelectedUnranked && (
                        <div className="flex gap-2">
                            <button onClick={() => { addToRanked(selectedRunnerId); setSelectedRunnerId(null); }} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 transition-all font-bold text-sm text-white shadow-sm">
                                <Plus size={16} /> Classer
                            </button>
                            <button onClick={() => setAsDnf(selectedRunnerId)} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-100 hover:bg-amber-50 transition-all font-bold text-sm text-slate-600">DNF</button>
                            <button onClick={() => setAsDns(selectedRunnerId)} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all font-bold text-sm text-slate-600">DNS</button>
                        </div>
                    )}

                    {/* Ranked: position + actions */}
                    {isSelectedRanked && (
                        <>
                            <div className="flex items-center justify-center gap-3">
                                <span className="text-sm font-medium text-slate-500">Position</span>
                                <input
                                    type="number" min={1} value={selectedPosition}
                                    onChange={(e) => { const v = parseInt(e.target.value, 10); if (v > 0) setRunnerPosition(selectedRunnerId, v); }}
                                    className="w-16 h-11 text-center text-xl font-black text-emerald-700 bg-emerald-50 border-2 border-emerald-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            </div>
                            <div className="flex items-center justify-center gap-2">
                                <button onClick={() => setAsDnf(selectedRunnerId)} className="h-9 px-4 rounded-xl text-xs font-bold bg-slate-100 text-slate-500 hover:bg-amber-50 hover:text-amber-600 flex items-center gap-1.5 transition-all">
                                    <AlertTriangle size={12} /> DNF
                                </button>
                                <button onClick={() => setAsDns(selectedRunnerId)} className="h-9 px-4 rounded-xl text-xs font-bold bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center gap-1.5 transition-all">
                                    <Ban size={12} /> DNS
                                </button>
                                <button onClick={() => clearRunner(selectedRunnerId)} className="h-9 px-3 rounded-xl text-xs font-bold text-red-400 bg-slate-100 hover:bg-red-50 hover:text-red-500 flex items-center gap-1.5 transition-all">
                                    <Trash2 size={12} /> Retirer
                                </button>
                            </div>
                        </>
                    )}

                    {/* DNF: reclasser + DNS + retirer */}
                    {isSelectedDnf && (
                        <>
                            <div className="flex items-center justify-center gap-3">
                                <span className="text-sm font-medium text-slate-500">Position</span>
                                <input
                                    type="number" min={1} placeholder="—"
                                    onChange={(e) => { const v = parseInt(e.target.value, 10); if (v > 0) setRunnerPosition(selectedRunnerId, v); }}
                                    className="w-16 h-11 text-center text-xl font-black text-emerald-700 bg-emerald-50 border-2 border-emerald-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            </div>
                            <div className="flex items-center justify-center gap-2">
                                <button onClick={() => setAsDns(selectedRunnerId)} className="h-9 px-4 rounded-xl text-xs font-bold bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center gap-1.5 transition-all">
                                    <Ban size={12} /> DNS
                                </button>
                                <button onClick={() => clearRunner(selectedRunnerId)} className="h-9 px-3 rounded-xl text-xs font-bold text-red-400 bg-slate-100 hover:bg-red-50 hover:text-red-500 flex items-center gap-1.5 transition-all">
                                    <Trash2 size={12} /> Retirer
                                </button>
                            </div>
                        </>
                    )}

                    {/* DNS: reclasser + DNF + retirer */}
                    {isSelectedDns && (
                        <div className="flex gap-2">
                            <button onClick={() => { addToRanked(selectedRunnerId); setSelectedRunnerId(null); }} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 transition-all font-bold text-sm text-white shadow-sm">
                                <Plus size={16} /> Classer
                            </button>
                            <button onClick={() => setAsDnf(selectedRunnerId)} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-100 hover:bg-amber-50 transition-all font-bold text-sm text-slate-600">DNF</button>
                            <button onClick={() => clearRunner(selectedRunnerId)} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-100 hover:bg-red-50 transition-all font-bold text-sm text-red-400">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    )}
                </RunnerActionSheet>
            )}
        </PageContent>
    );
}
